import React, { useState } from 'react';
import { clsx } from 'clsx';

/**
 * Sole-proprietor agencies often duplicate the agent's name (agent: "María
 * José Alonso", agency: "María José"). Show the agency line only when neither
 * name contains the other. Mirrors the check in mobile intercambio/propiedades.
 */
export function shouldShowAgencyName(agentName?: string | null, agencyName?: string | null): boolean {
    const a = (agentName ?? '').trim().toLowerCase();
    const g = (agencyName ?? '').trim().toLowerCase();
    return !!g && a !== g && !a.includes(g) && !g.includes(a);
}

interface ColleagueAvatarProps {
    name: string;
    avatar?: string | null;
    agencyLogo?: string | null;
    agencyName?: string | null;
    /** agencies.logo_bg_color — the logo's backdrop (white when unset), as in Vitrina. */
    agencyLogoBg?: string | null;
    /** Pixel size of the main tile (default 44). */
    size?: number;
    /** Rounded-square tile (cards) or circle (list column). */
    shape?: 'square' | 'circle';
    isLight?: boolean;
}

/**
 * Intercambio colleague avatar:
 *  - photo + logo → photo with the agency logo as a corner badge
 *  - logo only    → the agency logo fills the tile
 *  - neither      → initials
 * A URL that fails to load falls through to the next option.
 */
export const ColleagueAvatar: React.FC<ColleagueAvatarProps> = ({
    name, avatar, agencyLogo, agencyName, agencyLogoBg, size = 44, shape = 'square', isLight = true,
}) => {
    // White unless the agency set (or Settings sampled) a backdrop — white
    // logos on transparent PNGs need their dark one to be visible at all.
    const logoBg = agencyLogoBg || '#ffffff';
    const [avatarFailed, setAvatarFailed] = useState(false);
    const [logoFailed, setLogoFailed] = useState(false);
    const photo = avatar && !avatarFailed ? avatar : null;
    const logo = agencyLogo && !logoFailed ? agencyLogo : null;
    const radius = shape === 'circle' ? 'rounded-full' : 'rounded-xl';
    const badge = Math.round(size * 0.42);
    const initials = name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(w => w[0]?.toUpperCase() ?? '')
        .join('') || '?';

    return (
        <div className="relative shrink-0" style={{ width: size, height: size }}>
            {photo ? (
                <img
                    src={photo}
                    alt={name}
                    onError={() => setAvatarFailed(true)}
                    className={clsx("w-full h-full object-cover", radius, isLight ? "bg-slate-100" : "bg-slate-800")}
                />
            ) : logo ? (
                <div
                    className={clsx(
                        "w-full h-full flex items-center justify-center overflow-hidden border p-1",
                        radius,
                        isLight ? "border-slate-200" : "border-white/10"
                    )}
                    style={{ backgroundColor: logoBg }}
                >
                    <img
                        src={logo}
                        alt={agencyName || name}
                        onError={() => setLogoFailed(true)}
                        className="max-w-full max-h-full object-contain"
                    />
                </div>
            ) : (
                <div
                    className={clsx(
                        "w-full h-full flex items-center justify-center font-bold",
                        radius,
                        isLight ? "bg-indigo-100 text-indigo-700" : "bg-indigo-500/20 text-indigo-300"
                    )}
                    style={{ fontSize: Math.round(size * 0.36) }}
                >
                    {initials}
                </div>
            )}

            {photo && logo && (
                <div
                    className={clsx(
                        "absolute -bottom-1 -right-1 rounded-md flex items-center justify-center overflow-hidden ring-2",
                        isLight ? "ring-white border border-slate-200" : "ring-slate-900 border border-white/10"
                    )}
                    style={{ width: badge, height: badge, backgroundColor: logoBg }}
                    title={agencyName || undefined}
                >
                    <img
                        src={logo}
                        alt={agencyName || ''}
                        onError={() => setLogoFailed(true)}
                        className="max-w-full max-h-full object-contain p-px"
                    />
                </div>
            )}
        </div>
    );
};
